import { faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import type { IImportColumn, IPreviewRow } from '@feathr/blackbox';
import type { ITagListCellTag } from '@feathr/components';
import { TagListCell, Tooltip } from '@feathr/components';

/** Reorder columns in the order of preferred columns */
const reorderColumns = (columnMappers: IImportColumn[]): string[] => {
  let preferredColumns = ['email', 'name', 'first_name', 'last_name'];
  preferredColumns = preferredColumns.map((column) => {
    const preferredMapper = columnMappers.find((mapper) => mapper.feathr_attr === column);
    return preferredMapper ? preferredMapper.column_name : column;
  });

  const columns = columnMappers.map((mapping) => mapping.column_name);

  return columns.sort((a, b) => {
    let indexA = preferredColumns.indexOf(a);
    let indexB = preferredColumns.indexOf(b);
    if (indexA === -1) {
      indexA = preferredColumns.length;
    }
    if (indexB === -1) {
      indexB = preferredColumns.length;
    }
    return indexA - indexB;
  });
};

export default function previewTableColumns(
  t: TFunction,
  columnMappers: IImportColumn[],
  tags: ITagListCellTag[],
): Array<ColumnDef<IPreviewRow>> {
  const columns: Array<ColumnDef<IPreviewRow>> = reorderColumns(columnMappers).map(
    (columnName, index) => {
      return {
        id: columnName,
        header: columnName,
        /*
         * The first data column is the most identifying one available — reorderColumns sorts
         * email/name/first_name/last_name to the front. Keyed off position rather than the
         * attribute name, because the id is the CSV's own header text and none of those
         * attributes has to be mapped.
         *
         * `isFlex` gives it the width the other columns leave. `isPrimary` is here for the column
         * it keeps the 150-350px name range *off*: that range otherwise lands on whichever column
         * comes first, which is the 40px status icon unshifted in below.
         */
        meta: index === 0 ? { isFlex: true, isPrimary: true } : undefined,
        cell: ({ row }: CellContext<IPreviewRow, unknown>): string =>
          row.original.input[columnName],
      };
    },
  );

  // Prepend icon column that indicates whether the row will be imported or not
  columns.unshift({
    id: 'icon',
    header: '',
    size: 40,
    cell: ({ row }: CellContext<IPreviewRow, unknown>): JSX.Element =>
      row.original.error?.length ? (
        <Tooltip title={t('This row will not be imported, due to an error.')}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </Tooltip>
      ) : (
        <Tooltip title={t('This row will be imported.')}>
          <FontAwesomeIcon icon={faCheckCircle} />
        </Tooltip>
      ),
  });

  // Add in tags; the import's tags apply to every imported row, so the list is
  // identical in each cell.
  columns.push({
    id: 'tags',
    header: t('Tags'),
    cell: (): JSX.Element => <TagListCell tags={tags} />,
  });

  return columns;
}
